import { useCallback } from 'react';
import { values } from 'mobx';

import { usePersonalizedItems, Scenarios, GetRecommendationsFallback } from 'src/recommendations';
import { ProductCarouselProps } from 'src/components/product-carousel';

import useCart from 'src/hooks/use-cart';
import { useDecorateWithSponsoredProducts } from 'src/utils/ads/hooks/sponsored-products';
import { SponsoredProductsInventoryId } from 'types/graphql';
import { useLegacyCarouselsEnabled } from 'src/utils/personalization/hooks/use-personalization-enabled';
import { useAdsEnabled } from 'src/utils/ads/hooks/use-ads-enabled';

import { Category } from 'src/generics/data/generic-category';
import { OFTEN_PURCHASED_WITH_FILTERS } from 'src/components/carousels';

type UseCartRecommendationsReturn = Omit<ProductCarouselProps, 'heading'>;

function useGetProductsFallback(type = Category.flower): GetRecommendationsFallback {
  const types = OFTEN_PURCHASED_WITH_FILTERS[type];

  return useCallback<GetRecommendationsFallback>((getProducts) => getProducts({ types, sortBy: `type` }), [types]);
}

export function useCartRecommendationsCarousel(): UseCartRecommendationsReturn {
  const { items: cartItems } = useCart();

  const cartItemsArray = values(cartItems);
  const latestProduct = cartItemsArray.slice(-1)[0]?.product;
  const productIds = cartItemsArray.map((item) => item.id);
  const adsEnabled = useAdsEnabled();
  const skipAds = !adsEnabled;
  const isLegacyCarouselsEnabled = useLegacyCarouselsEnabled();

  const getProductsFallback = useGetProductsFallback(latestProduct?.type);

  const { data: organicRecommendations, loading: organicLoading } = usePersonalizedItems(
    getProductsFallback,
    productIds,
    Scenarios.cart1,
    {
      skip: !isLegacyCarouselsEnabled || !productIds.length,
      limit: 25,
    }
  );

  const { products: sponsoredAndOrganicProducts, loading: sponsoredLoading } = useDecorateWithSponsoredProducts({
    skip: skipAds,
    products: organicRecommendations ?? [],
    loading: organicLoading,
    error: undefined,
    limit: 5,
    inventoryId: SponsoredProductsInventoryId.cartTopperProductAuc,
    searchQuery: undefined,
  });

  const hideSponsoredTags = skipAds || !sponsoredAndOrganicProducts.some((product) => !!product.adTrackers);

  return {
    products: sponsoredAndOrganicProducts,
    loading: organicLoading || sponsoredLoading,
    placement: 'cart-drawer-cart-recommendations-carousel',
    hideSponsoredTags,
  };
}
